import type { TPrString } from 'data/schema';
import type { SessionState } from './computed';

/**
 * AVS-4462: keeps a root `prString` that `applyRemoteSession` derived from a broadcast away from the
 * 3D deck. The deck calls `selectCar` on the AVE for every `pr-string` change, and the server strips
 * root `prString` from 3D broadcasts precisely so viewers do not all do that at once.
 *
 * `deck` is the value the mounted deck element was given; with no deck mounted it equals `derived`,
 * so the next deck mounts on root. `passed` lists, in order, the cars this hold moved through, and
 * `deckAt` is the deck's position in it (-1 when the deck was given `derived`). The AVE reports cars
 * in the order they were picked, so an announcement of a car ahead of `deckAt` is the deck catching
 * up (stale), and one at or behind it is a new pick.
 */
export interface IDeckPrStringHold {
  readonly deck: TPrString | undefined;
  readonly derived: TPrString;
  readonly passed: readonly TPrString[];
  readonly deckAt: number;
}

let hold: IDeckPrStringHold | null = null;
let mountedDecks = 0;
const listeners = new Set<() => void>();

function setHold(next: IDeckPrStringHold | null): void {
  if (next === hold) return;
  hold = next;
  listeners.forEach((listener) => listener());
}

export function subscribeDeckPrString(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getDeckPrStringHold(): IDeckPrStringHold | null {
  return hold;
}

/** The `pr-string` the deck should render for `root`. */
export function heldDeckPrString(
  root: TPrString | undefined,
  current: IDeckPrStringHold | null = hold
): TPrString | undefined {
  return current && root === current.derived ? current.deck : root;
}

/**
 * AVS-4481: the `model-revision` the deck should render for `root`. While AVS-4462 holds the deck's
 * `pr-string` on an older car, the deck must not be handed the root's revision, or the `pr-string` /
 * `model-revision` pair would describe two different cars. In that case the revision comes from the
 * registry vehicle whose `prString` is the held one. If that vehicle cannot be resolved (its entry was
 * pruned or removed), the revision is omitted rather than falling back to root's, which would recreate
 * the same invalid pairing this helper exists to prevent.
 */
export function heldDeckModelRevision(
  root: { prString?: TPrString; modelRevision?: string },
  vehicles: Readonly<Record<string, { prString?: TPrString; modelRevision?: string }>> | undefined,
  current: IDeckPrStringHold | null = hold
): string | undefined {
  const held = heldDeckPrString(root.prString, current);
  if (held === root.prString) return root.modelRevision;
  const vehicle = Object.values(vehicles ?? {}).find((candidate) => candidate.prString === held);
  return vehicle?.modelRevision;
}

/** Records a root `prString` derived from a broadcast. Call it before the root write. */
export function holdDeckPrString(previousRoot: TPrString | undefined, derived: TPrString): void {
  // A derivation on top of the last one extends it: the deck still has the car the chain started from.
  const chain = hold && hold.derived === previousRoot ? hold : null;
  const passed = previousRoot ? [...(chain?.passed ?? []), previousRoot] : [...(chain?.passed ?? [])];
  if (mountedDecks === 0) {
    setHold({ deck: derived, derived, passed, deckAt: -1 });
    return;
  }
  // A chain whose deck was given `derived` (no deck then) has that deck on `previousRoot` now.
  const deck = chain ? chain.deck : previousRoot;
  const deckAt = chain && chain.deckAt >= 0 ? chain.deckAt : previousRoot ? passed.length - 1 : -1;
  setHold({ deck, derived, passed, deckAt });
}

/**
 * Reports a deck `update:pr-string`. Returns true when it is a late announcement of a car the hold
 * already moved past (ahead of the deck in the chain) or a re-announce of the deck's own car, which the
 * caller must not apply to the session.
 */
export function announceDeckPrString(prString: TPrString | undefined): boolean {
  if (!prString || !hold) return false;
  // An earlier occurrence still ahead of the deck comes first, even when it is also the derived car.
  const at = hold.passed.indexOf(prString, hold.deckAt + 1);
  if (at !== -1) {
    setHold({ ...hold, deck: prString, deckAt: at });
    return true;
  }
  if (prString === hold.derived) {
    setHold(null);
    return false;
  }
  // AVS-4248: the deck re-announces its current car on (re-)mount; that is not a pick.
  return prString === hold.deck;
}

export function releaseDeckPrString(): void {
  setHold(null);
}

export function noteDeckMounted(): void {
  mountedDecks += 1;
}

export function noteDeckUnmounted(): void {
  mountedDecks = Math.max(0, mountedDecks - 1);
  if (mountedDecks === 0 && hold && hold.deck !== hold.derived) {
    setHold({ ...hold, deck: hold.derived, deckAt: -1 });
  }
}

/** Ends the hold when root `prString` moves to anything but the derived car. */
export function initDeckPrStringRelease(state: SessionState): void {
  state.on('change', (changedKeys, changedValues) => {
    if (!hold) {
      return;
    }
    const index = changedKeys.indexOf('prString');
    if (index !== -1 && changedValues[index] !== hold.derived) {
      releaseDeckPrString();
    }
  });
}
